import { createSelector } from 'reselect';

import {
  selectEntitiesAchievementsEntityEligibilityAllowedCategoryIds,
  selectEntitiesAchievementsEntityEligibilityAllowedMatchIds,
  selectEntitiesAchievementsEntityEligibilityAllowedSportIds,
  selectEntitiesAchievementsEntityEligibilityAllowedTournamentIds,
} from '../../../../selectors/entities/achievements/entitiesAchievementsPrimary.selector';
import { selectEntitiesCategories } from '../../../../selectors/entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesSports } from '../../../../selectors/entities/sports/entitiesSportsPrimary.selector';
import {
  selectUserMissionsModerator,
  selectUserPromotionStatus,
} from '../../../../selectors/user/userPrimary.selector';
import { StateType } from '../../../../states';
import { PromotionStatuses } from '../../../../types/promotions';
import { Selector } from '../../../../types/selectors';
import { handleInternalNavigation } from '../../../../utils/notification';
import { selectAchievementNbBetsToTry } from '../../selectors/achievements';
import {
  selectMissionAchievementDescription,
  selectMissionAchievementMinBetOdds,
  selectMissionAchievementMinPlacedAmount,
  selectMissionAchievementReward,
} from '../../selectors/missionAchievements';
import { selectUserMissionAchievementIsWon } from '../../selectors/userMissionAchievements';
import { AchievementReward } from '../../types/state/achievements';

const getHistoryFromProps = (_: StateType, { history }: { history?: unknown }) => history;
const getDispatchFromProps = (_: StateType, { dispatch }: { dispatch?: unknown }) => dispatch;

const selectAchievementOnClick: Selector<(() => void) | undefined> = createSelector(
  [
    selectEntitiesAchievementsEntityEligibilityAllowedMatchIds,
    selectEntitiesAchievementsEntityEligibilityAllowedTournamentIds,
    selectEntitiesAchievementsEntityEligibilityAllowedCategoryIds,
    selectEntitiesAchievementsEntityEligibilityAllowedSportIds,
    selectEntitiesSports,
    selectEntitiesCategories,
    getHistoryFromProps,
    getDispatchFromProps,
    selectUserPromotionStatus,
    selectUserMissionsModerator,
  ],
  (
    allowedMatchIds,
    allowedTournamentIds,
    allowedCategoryIds,
    allowedSportIds,
    sports,
    categories,
    history,
    dispatch,
    promotionStatus,
    missionsModerator,
  ) => {
    if (!missionsModerator && promotionStatus === PromotionStatuses.DEFAULT) {
      if (allowedMatchIds.length === 1) {
        const first = allowedMatchIds[0];

        if (first) {
          return () => {
            handleInternalNavigation({ history, dispatch, uri: `wam://betting?target=match-${first}` });
          };
        }
      } else if (allowedTournamentIds.length === 1) {
        const first = allowedTournamentIds[0];

        if (first) {
          const categoryIds = Object.keys(categories).map((categoryIdAsKey) => parseInt(categoryIdAsKey, 10));
          const categoryId = categoryIds.find(
            (categoryId) => (categories[categoryId]?.tournaments?.indexOf(first) ?? -1) !== -1,
          );

          if (categoryId) {
            const sportIds = Object.keys(sports).map((sportIdAsKey) => parseInt(sportIdAsKey, 10));
            const sportId = sportIds.find((sportId) => (sports[sportId]?.categories?.indexOf(categoryId) ?? -1) !== -1);

            if (sportId) {
              return () => {
                handleInternalNavigation({
                  history,
                  dispatch,
                  uri: `wam://betting?target=catalog-${sportId}-${categoryId}-${first}`,
                });
              };
            }
          }

          return () => {
            handleInternalNavigation({ history, dispatch, uri: `wam://betting?target=match-${first}` });
          };
        }
      } else if (allowedCategoryIds.length === 1) {
        const first = allowedCategoryIds[0];

        if (first) {
          const sportIds = Object.keys(sports).map((sportIdAsKey) => parseInt(sportIdAsKey, 10));
          const sportId = sportIds.find((sportId) => (sports[sportId]?.categories?.indexOf(first) ?? -1) !== -1);

          if (sportId) {
            return () => {
              handleInternalNavigation({
                history,
                dispatch,
                uri: `wam://betting?target=catalog-${sportId}-${first}`,
              });
            };
          }
        }
      } else if (allowedSportIds.length === 1) {
        const first = allowedSportIds[0];

        if (first) {
          return () => {
            handleInternalNavigation({ history, dispatch, uri: `wam://betting?target=catalog-${first}` });
          };
        }
      } else {
        return () => {
          handleInternalNavigation({
            history,
            dispatch,
            uri: `wam://betting`,
          });
        };
      }
    }
  },
);

type MissionDetailsAchievementContainerPropsType = {
  description?: string;
  minBetOdds?: number;
  minPlacedAmount: number;
  reward?: AchievementReward | null;
  isWon: boolean;
  showProgress: boolean;
  onClick?: () => void;
};

const selectMissionDetailsAchievementContainerProps: Selector<MissionDetailsAchievementContainerPropsType> =
  createSelector(
    [
      selectMissionAchievementDescription,
      selectMissionAchievementMinBetOdds,
      selectMissionAchievementMinPlacedAmount,
      selectMissionAchievementReward,
      selectUserMissionAchievementIsWon,
      selectAchievementNbBetsToTry,
      selectAchievementOnClick,
    ],
    (description, minBetOdds, minPlacedAmount, reward, isWon, nbBetsToTry, onClick) => ({
      description,
      minBetOdds,
      minPlacedAmount: (minPlacedAmount ?? 0) * 100,
      reward,
      isWon,
      showProgress: typeof nbBetsToTry === 'number' ? nbBetsToTry > 1 : false,
      onClick,
    }),
  );

export default selectMissionDetailsAchievementContainerProps;
